import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import type { User, Permission, UserRole } from "@/types";
import api from "@/services/api";

interface AuthState {
  user: User | null;
  token: string | null;
  permissions: Permission[];
  isAuthenticated: boolean;
}

// Helper functions for localStorage persistence
const STORAGE_KEY = "auth_state";

const loadAuthFromStorage = (): AuthState => {
  try {
    const storedAuth = localStorage.getItem(STORAGE_KEY);
    if (storedAuth) {
      const parsedAuth = JSON.parse(storedAuth);
      return {
        user: parsedAuth.user || null,
        token: parsedAuth.token || null,
        permissions: parsedAuth.permissions || [],
        isAuthenticated: !!parsedAuth.token && !!parsedAuth.user,
      };
    }
  } catch (error) {
    console.error("Failed to load auth state from storage:", error);
  }
  return {
    user: null,
    token: null,
    permissions: [],
    isAuthenticated: false,
  };
};

const initialState: AuthState = loadAuthFromStorage();

// Async thunk to refresh user permissions
export const refreshPermissions = createAsyncThunk(
  "auth/refreshPermissions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/v1/auth/permissions");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to refresh permissions",
      );
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{
        user: User;
        token: string;
        permissions: Permission[];
      }>,
    ) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.permissions = action.payload.permissions;
      state.isAuthenticated = true;

      // Persist to localStorage
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          user: action.payload.user,
          token: action.payload.token,
          permissions: action.payload.permissions,
        }),
      );
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.permissions = [];
      state.isAuthenticated = false;

      // Clear localStorage
      localStorage.removeItem(STORAGE_KEY);
    },
    restoreAuth: (
      state,
      action: PayloadAction<{
        user: User;
        token: string;
        permissions: Permission[];
      }>,
    ) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.permissions = action.payload.permissions;
      state.isAuthenticated = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(refreshPermissions.fulfilled, (state, action) => {
      state.permissions = action.payload;

      // Update localStorage
      const storedAuth = localStorage.getItem(STORAGE_KEY);
      if (storedAuth) {
        const parsedAuth = JSON.parse(storedAuth);
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            ...parsedAuth,
            permissions: action.payload,
          }),
        );
      }
    });
  },
});

export const { setCredentials, logout, restoreAuth } = authSlice.actions;
export default authSlice.reducer;
